// PWA push opt-in bottom sheet. Shown after the user has installed the PWA
// (display-mode: standalone) and not yet granted/denied notification permission.
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { pushSubscribe } from "@/lib/push-public.functions";
import { VAPID_PUBLIC_KEY } from "@/lib/notifications-shared";
import { storeResubscribeToken } from "@/knowmeno/pushTokenStore";

const PL = "#4B1F4F";
const GR = "#6E4B71";
const BG = "#FAF7F5";
const WH = "#ffffff";

const DISMISS_KEY = "menoiq_push_dismissed_at";
const NEVER_KEY = "menoiq_push_never";
const DISMISS_WINDOW_DAYS = 7;

function safeGet(k: string): string | null {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string) {
  try {
    window.localStorage.setItem(k, v);
  } catch {}
}
function recentlyDismissed(): boolean {
  const ts = safeGet(DISMISS_KEY);
  if (!ts) return false;
  const t = Date.parse(ts);
  if (Number.isNaN(t)) return false;
  return (Date.now() - t) / 86400000 < DISMISS_WINDOW_DAYS;
}
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function PushOptInPrompt({ forceOpen, onClose }: { forceOpen?: boolean; onClose?: () => void } = {}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subscribeFn = useServerFn(pushSubscribe);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (forceOpen) {
      setOpen(true);
      return;
    }

    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      inIframe = true;
    }
    const host = window.location.hostname;
    const isPreviewHost =
      host.includes("id-preview--") || host.includes("lovableproject.com");
    if (inIframe || isPreviewHost) return;

    const standalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      (navigator as any).standalone === true;
    if (!standalone) return;
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
    if (Notification.permission !== "default") return;
    if (safeGet(NEVER_KEY) === "1") return;
    if (recentlyDismissed()) return;

    // Slight delay so it doesn't fight with the install prompt
    const t = window.setTimeout(() => setOpen(true), 1200);
    return () => window.clearTimeout(t);
  }, [forceOpen]);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    onClose?.();
  };
  const notNow = () => {
    safeSet(DISMISS_KEY, new Date().toISOString());
    close();
  };
  const never = () => {
    safeSet(NEVER_KEY, "1");
    close();
  };

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setBusy(false);
        notNow();
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      // Clear any cached subscription (e.g. from a previous VAPID key) before
      // subscribing — the browser otherwise rejects subscribe() with
      // "A subscription with a different applicationServerKey already exists".
      try {
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          await existing.unsubscribe();
        }
      } catch (e) {
        console.warn("[PushOptInPrompt] failed to clear existing subscription", e);
      }
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
      });
      const json = sub.toJSON() as any;
      if (!json?.endpoint || !json?.keys?.p256dh || !json?.keys?.auth) {
        throw new Error("Browser returned an incomplete push subscription.");
      }
      const result = await subscribeFn({
        data: {
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
          userAgent: navigator.userAgent.slice(0, 256),
        },
      });
      if (!result?.ok || !result?.id) {
        throw new Error("Server did not confirm push subscription was saved.");
      }
      // Persist the per-subscription resubscribe token so the service worker
      // can present it on pushsubscriptionchange.
      try {
        if (result.resubscribeToken) {
          await storeResubscribeToken(json.endpoint, result.resubscribeToken);
        }
      } catch (e) {
        console.warn("[PushOptInPrompt] failed to persist resubscribe token", e);
      }
      close();
    } catch (e: any) {
      console.error("[PushOptInPrompt] enable() failed:", e);
      try {
        (window as unknown as { __sentryCapture?: (e: unknown) => void })
          .__sentryCapture?.(e);
      } catch {}
      setError(e?.message || "Couldn't enable push. You can try again later.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Enable push notifications"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9100,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(20, 8, 18, 0.45)",
        fontFamily: "system-ui, sans-serif",
      }}
      onClick={notNow}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          margin: "0 12px 12px",
          background: BG,
          borderRadius: 24,
          padding: "22px 22px 18px",
          boxShadow: "0 30px 80px -30px rgba(74, 25, 66, 0.45)",
        }}
      >
        <div
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: 24,
            color: PL,
            fontWeight: 500,
            letterSpacing: "-0.01em",
            marginBottom: 8,
          }}
        >
          Get instant reminders
        </div>
        <p style={{ color: GR, fontSize: 14, lineHeight: 1.5, margin: "0 0 16px" }}>
          We can send notifications directly to your phone for daily check-ins,
          HRT reminders, and milestones. Tap allow on the next prompt.
        </p>
        {error ? (
          <p style={{ color: "#B0263F", fontSize: 12.5, margin: "0 0 12px" }}>{error}</p>
        ) : null}
        <button
          disabled={busy}
          onClick={enable}
          style={{
            width: "100%",
            background: PL,
            color: WH,
            border: "none",
            borderRadius: 14,
            padding: 14,
            fontSize: 15,
            fontWeight: 500,
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.7 : 1,
            marginBottom: 10,
          }}
        >
          {busy ? "Enabling…" : "Enable"}
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            onClick={notNow}
            style={{
              background: "transparent",
              color: PL,
              border: `1px solid ${PL}`,
              borderRadius: 12,
              padding: "10px 14px",
              fontSize: 13.5,
              cursor: "pointer",
            }}
          >
            Not now
          </button>
          <button
            onClick={never}
            style={{
              background: "transparent",
              color: GR,
              border: "none",
              padding: "8px 4px",
              fontSize: 12.5,
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Don't ask again
          </button>
        </div>
      </div>
    </div>
  );
}

export default PushOptInPrompt;
